/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Irrevaldy
*/

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { isWeb } from '@global/screens';
import { CHECK_SIZE } from '@/constants';
import Colors from '@global/colors';
import {
  getSavedFilter,
  splitProductByCombination,
  convertProductMarketType,
} from '@global/helpers';
import Image from '@components/global/Image';
import SociollaText from '@components/sociolla/SociollaText';
import SelectBox from '@components/form/SelectBox';
import ModalHalf from '@components/modal/ModalHalf';
import Table from '@components/table/Table';
import ListProductBrand from '@/screens/catalog/components/ListProductBrand';
import * as Layout from '@components/screen/Layout';
import ModalDetailProduct from '@components/transaction/catalog/ModalDetailProduct';
import ProductItemMobile from '@/screens/catalog/components/ProductItemMobile';

import useApi from '@/hooks/useApi';
import { MsCatalog } from '@api/ms-catalog';
import store from '@/store';
import { setProductData } from '@/store/product';
import { paramsReseted, paginationAdded } from '@/store/params';

const getCategoryPath = (product, selectedCategoryId = '') => {
  const availableCategories = Array.isArray(product?.categories)
    ? product.categories.filter((category) => category?.id)
    : [];

  if (!availableCategories.length) {
    return '';
  }

  const visibleCategories = availableCategories.filter(
    (category) => !category.is_show_category && !category.is_promotion,
  );

  const categoriesForPath = visibleCategories.length
    ? visibleCategories
    : availableCategories;

  const selectedIndex = categoriesForPath.findIndex(
    (category) => String(category?.id) === String(selectedCategoryId),
  );

  const childCategories =
    selectedIndex >= 0
      ? categoriesForPath.slice(selectedIndex + 1)
      : categoriesForPath;

  const categoryPath = childCategories
    .slice(0, 2)
    .map((category) => category?.name?.trim())
    .filter(Boolean)
    .join('/');

  return categoryPath || '';
};

const tableStructure = {
  image: {
    head: 'Product Image',
    body: 'image',
    type: 'image',
  },
  reference: {
    head: 'Internal Reference',
    body: 'reference',
    type: 'slot',
  },
  name: {
    head: 'Name',
    body: 'name',
  },
  variant: {
    head: 'Attribute Value',
    body: 'variant',
  },
  category: {
    head: 'Category',
    body: 'categoryPath',
  },
  price: {
    head: 'Price',
    body: 'price',
    type: 'currency',
  },
  attribute: {
    head: 'Attribute',
    body: 'attribute',
    type: 'slot',
  },
};

const SORTS = [
  {
    label: isWeb ? 'All Product' : 'Name',
    value: isWeb ? 'all' : 'name',
  },
  {
    label: 'Best Seller',
    value: 'best-seller',
  },
  {
    label: 'New Arrival',
    value: 'new-arrival',
  },
];

const defaultSort = isWeb ? SORTS[0] : '';

const CatalogCategoriesScreen = (props) => {
  const dispatch = useDispatch();
  const paramsState = useSelector((state) => state.params);
  const productDetail = useSelector((state) => state.product.productData);

  const [totalData, setTotalData] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedSort, setSelectedSort] = useState(defaultSort);
  const [showSort, setShowSort] = useState(false);

  useEffect(() => {
    setSearchValue('');
    setLoading(true);
    dispatch(paramsReseted());

    getSavedFilter('CatalogCategoriesScreen').then((res) => {
      const saved = res?.name?.['$regex'] || '';
      setSearchValue(saved);
      dispatch(paramsReseted());
      doSearch(saved, 0);
    });

    return () => {
      dispatch(paramsReseted());
    };
  }, [props?.route?.params?.id]);

  const getProducts = useApi(MsCatalog.getProducts);
  const getProductCount = useApi(MsCatalog.getProductCount);

  const doSearch = async (val = '', newSkip = null) => {
    const storeParams = JSON.parse(JSON.stringify(store.getState().params));
    const currentSkip =
      typeof newSkip === 'number' ? newSkip : storeParams.skip;
    const isLoadMore = currentSkip > 0 && !isWeb;

    try {
      setSearchValue(val);

      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const params = {
        params: {
          filter: {},
          skip: currentSkip,
          limit: storeParams.limit,
        },
      };

      if (props?.route?.params?.id) {
        params.params.filter['categories.id'] = props.route.params.id;
      }

      params.params.sort = 'name';

      const selectedFilter = selectedSort?.value;

      if (!isWeb && selectedFilter !== 'name') {
        params.params.sort = selectedFilter;
      }

      if (val) {
        if (['best-seller', 'new-arrival'].includes(selectedFilter)) {
          params.params.filter['product.name'] = {
            $regex: val,
            $options: 'i',
          };
        } else {
          params.params.filter.name = {
            $regex: val,
            $options: 'i',
          };
        }
      }

      if (currentSkip === 0) {
        const countResponse = await getProductCount.request(params);
        if (countResponse?.data?.data) {
          setTotalData(countResponse.data.data);
        } else {
          setTotalData(0);
        }
      }

      const response = await getProducts.request(params);

      if (response?.data?.data?.length) {
        const newResponse = splitProductByCombination(
          response.data.data,
          true,
        ).map((pr) => {
          const converted = convertProductMarketType(pr);

          return {
            ...converted,
            categoryPath: getCategoryPath(converted, props?.route?.params?.id),
          };
        });

        if (isLoadMore) {
          setTableData((prev) => prev.concat(newResponse));
        } else {
          setTableData(newResponse);
        }
      } else if (!isLoadMore) {
        setTableData([]);
      }

      setLoading(false);
      if (isLoadMore) {
        setLoadingMore(false);
      }
    } catch (e) {
      setLoading(false);
      if (isLoadMore) {
        setLoadingMore(false);
      }
      console.log(e);
    }
  };

  const gotToDetail = (data) => {
    dispatch(setProductData(data));
  };

  const onCloseDetail = () => {
    dispatch(setProductData({}));
  };

  return (
    <Layout.Container>
      {isWeb ? (
        <>
          <Layout.Back
            title="Product Catalog"
            onCallback={() => {
              props.navigation.navigate('CatalogScreen', {
                tab: 'categories',
              });
            }}
          />

          <Table
            name="catalog-product-brand"
            structures={tableStructure}
            data={tableData}
            loading={loading}
            hasCallback
            hasNoHide
            showFilter={false}
            totalData={totalData}
            skip={paramsState.skip}
            limit={paramsState.limit}
            searchKey="name"
            searchValue={searchValue}
            searchPlaceholder="Search by name..."
            onClickRow={({ data }) => gotToDetail(data)}
            onSearch={(val) => doSearch(val)}
            moreInlineFilter={
              <View>
                <SelectBox
                  label="Sort"
                  hideLabel
                  options={SORTS}
                  value={selectedSort}
                  placeholder="Select Sort"
                  trackBy="value"
                  labelKey="label"
                  isDropdown={isWeb}
                  onChange={async (value) => {
                    await dispatch(
                      paginationAdded({ skip: 0, limit: paramsState.limit }),
                    );
                    setSelectedSort(value);
                  }}
                />
              </View>
            }
          />
        </>
      ) : (
        <>
          <ListProductBrand
            name="catalog-product-brand"
            searchKey="name"
            placeholder="Search by name"
            loading={loading}
            onSearch={(value) => doSearch(value)}
            data={tableData}
            onFilter={() => setShowSort(true)}
            hasFilter={Boolean(selectedSort)}
            withLoadMore
            withFetch
            loadingPagination={loadingMore}
            totalData={totalData}
            skip={paramsState.skip}
            limit={paramsState.limit}
            onLoadMore={(newSkip) => doSearch(searchValue, newSkip)}
            renderItem={({ data, index }) => (
              <ProductItemMobile
                data={data}
                onCallback={() => gotToDetail(data)}
                extraInfo={data.brand?.name}
                key={index}
              />
            )}
          />

          <ModalHalf
            title={'Sort'}
            visible={showSort}
            onClose={() => setShowSort(false)}
            maxWidth={'100%'}
            headerBordered>
            {SORTS.map((item, idx) => (
              <TouchableOpacity
                style={styles.optionItem}
                activeOpacity={0.8}
                key={idx}
                onPress={() => {
                  setSelectedSort(item);
                  setShowSort(false);
                  dispatch(
                    paginationAdded({ skip: 0, limit: paramsState.limit }),
                  );
                }}>
                <View style={styles.checklist}>
                  {item.value == selectedSort?.value ||
                  (!selectedSort?.value && item.value == 'name') ? (
                    <Image
                      style={CHECK_SIZE}
                      width={CHECK_SIZE.width}
                      height={CHECK_SIZE.height}
                      svg
                      source={require('@assets/img/icons/check-circle-blue.svg')}
                    />
                  ) : (
                    false
                  )}
                </View>
                <SociollaText>{item.label}</SociollaText>
              </TouchableOpacity>
            ))}
          </ModalHalf>
        </>
      )}

      <ModalDetailProduct
        back={!isWeb}
        withPromotion
        title="Product Detail"
        show={Boolean(productDetail && Object.keys(productDetail).length)}
        onClose={() => onCloseDetail()}
      />
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  optionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 15,
    borderBottomColor: Colors.grey0,
    borderBottomWidth: 1,
  },
  checklist: {
    width: CHECK_SIZE.width + 1,
    height: CHECK_SIZE.width + 1,
    borderRadius: CHECK_SIZE.width + 1,
    borderColor: Colors.grey0,
    borderWidth: 1,
    marginRight: 15,
  },
});

export default CatalogCategoriesScreen;
